import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BasePermissionMenu } from "app/based/baseApiModel/basePermissionMenu/BasePermissionMenu";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { RoleManagementInjection } from "features/roleManagement/di/RoleManagementInjection";
import { RoleActionRequestEntity } from "features/roleManagement/domain/entities/actions/RoleActionRequestEntity";
import { DetailRoleEntity } from "features/roleManagement/domain/entities/detail/DetailRoleEntity";
import { RoleRequestEntity } from "features/roleManagement/domain/entities/RoleRequestEntity";
import { RoleResponseEntity } from "features/roleManagement/domain/entities/RoleResponseEntity";
import { RoleManagementUseCase } from "features/roleManagement/domain/useCases/RoleManagementUseCase";
import { RefObject } from "react";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";

class RoleInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public fetchList = new BehaviorSubject<RoleRequestEntity>({});
  public fetchDetail = new BehaviorSubject<RoleRequestEntity>({});
  public fetchMenu = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchFeatures = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchUsers = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    action?: "create" | "update" | "delete";
    value?: RoleActionRequestEntity;
    detailInfo?: DetailRoleEntity;
  }>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchMenu.complete();
    this.fetchFeatures.complete();
    this.fetchUsers.complete();
    this.fetchActions.complete();
  }
}

class RoleOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<RoleResponseEntity>>([]);
  public savedDetail = new BehaviorSubject<DetailRoleEntity>({});
  public savedMenus = new BehaviorSubject<Array<BasePermissionMenu>>([]);
  public savedFeatures = new BehaviorSubject<
    Array<BaseFilterListResponseModel>
  >([]);
  public savedUsers = new BehaviorSubject<Array<BaseUserResponseModel>>([]);
  public savedPaging = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public onActionSuccess = new BehaviorSubject<{
    type?: string;
    body?: RoleActionRequestEntity;
  }>({});

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.savedMenus.complete();
    this.savedFeatures.complete();
    this.savedUsers.complete();
    this.savedPaging.complete();
    this.onError.complete();
    this.onActionSuccess.complete();
  }
}

@injectable()
export class RoleManagementViewModel extends BaseViewModel<
  RoleInput,
  RoleOutput
> {
  private roleUseCase: RoleManagementUseCase;

  constructor() {
    super(new RoleInput(), new RoleOutput());
    this.roleUseCase = RoleManagementInjection.getUseCase();
  }

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: RefObject<FormInstance<any>>,
    prevParams: RoleRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : "createdAt DESC";

    const query = {
      ...prevParams,
      ...rest,
      created: created ?? prevParams?.created,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.savedList.next([]);
        this.input.loading.next(true);
        const res = await this.roleUseCase.getList(params);
        if (res?.data) {
          this.output.savedList.next(res.data);
          this.output.savedPaging.next(res.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.savedList.next([]);
        this.output.savedPaging.next({});
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.roleUseCase.getRoleDetail(params);
        if (res?.data) {
          this.output.savedDetail.next(res.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchFeatures, async (params) => {
      try {
        const res = await this.roleUseCase.getUserPermission(params);
        if (res) {
          this.output.savedFeatures.next(res);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchMenu, async (params) => {
      try {
        const res = await this.roleUseCase.getMenuList(params);
        if (res) {
          this.output.savedMenus.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchUsers, async (params) => {
      try {
        const res = await this.roleUseCase.getUsers(params);
        if (res) {
          this.output.savedUsers.next(res);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (params) => {
      try {
        this.input.loading.next(true);
        const checkedBody = this.roleUseCase.updateBody(
          params?.value,
          params?.detailInfo,
        );

        if (params?.action === "create" && params?.value) {
          const res = await this.roleUseCase.createRole(
            params.value,
            params?.detailInfo,
          );
          if (res?.data) {
            this.output.onActionSuccess.next({
              type: "create",
              body: checkedBody?.afterBody,
            });
          }
        }

        if (params?.action === "update" && params?.value) {
          const res = await this.roleUseCase.updateRole(
            params.value,
            params?.detailInfo,
          );
          if (res?.data) {
            this.output.onActionSuccess.next({
              type: "update",
              body: checkedBody?.afterBody,
            });
          }
        }

        if (params?.action === "delete" && params?.value?.code) {
          const res = await this.roleUseCase.deleteRole({
            code: params.value.code,
          });
          if (res?.data) {
            this.output.onActionSuccess.next({
              type: "delete",
              body: {},
            });
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });
  }
}
